import { useTabs } from 'hooks/useTabs';
import { useTranslation } from 'react-i18next';
import { AttachOilAnalysisTable } from '../OilAnalysis/AttachOilAnalysisTable';
import { useOilAnalysis } from 'components/Evidences/hooks/useOilAnalysis';
import { createDateOptions } from 'components/ExternalReferencesTables/Filters/filterOptions';
import './EvidenceTabs.scss';
import { AttachVibrationAnalysisTable } from '../VibrationAnalysis/AttachVibrationAnalysisTable';
import { useVibrationAnalysis } from '../../hooks/useVibrationAnalysis';
import { AnalysisDateFilters } from '../AnalysisDateFilters';
import { UploadFiles } from '../UploadFiles/UploadFiles';
import { useFeatureFlag } from '@shape/feature-flag';
import { UseFormHandleSubmit } from 'react-hook-form';
import { CaseForm } from 'queries/cases/types';

type EvidenceTabType = 'OIL_ANALYSIS' | 'VIBRATION_ANALYSIS' | 'UPLOAD';

interface EvidenceTabsProps {
  assetId: string;
  caseId: string;
  handleSubmit: UseFormHandleSubmit<CaseForm>;
}

export const EvidenceTabs = ({ assetId, caseId, handleSubmit }: EvidenceTabsProps) => {
  const { t } = useTranslation();
  const isOilEnabled = useFeatureFlag('monitoring-oil-analysis');
  const isVibrationEnabled = useFeatureFlag('monitoring-vibration-analysis');

  const TABS: EvidenceTabType[] = [
    ...(isOilEnabled ? (['OIL_ANALYSIS'] as const) : []),
    ...(isVibrationEnabled ? (['VIBRATION_ANALYSIS'] as const) : []),
    'UPLOAD',
  ];

  const { activeTab, tabsComponent } = useTabs({
    tabNames: TABS,
    getLabel: (tab) => t(`monitoring:tables.tabsLabels.${tab}`),
  });

  const oilAnalysisData = useOilAnalysis(assetId, activeTab);
  const vibrationAnalysisData = useVibrationAnalysis(assetId, activeTab);
  const dateOptions = createDateOptions(t);

  const showDateFilters = activeTab === 'OIL_ANALYSIS' || activeTab === 'VIBRATION_ANALYSIS';

  const tabActive = () => {
    switch (activeTab) {
      case 'OIL_ANALYSIS':
        return <AttachOilAnalysisTable assetId={assetId} oilAnalysisData={oilAnalysisData ?? {}} />;
      case 'VIBRATION_ANALYSIS':
        return (
          <AttachVibrationAnalysisTable
            assetId={assetId}
            vibrationAnalysisData={vibrationAnalysisData ?? {}}
          />
        );
      case 'UPLOAD':
        return <UploadFiles caseId={caseId} handleSubmit={handleSubmit} />;
      default:
        return false;
    }
  };

  return (
    <>
      <div className="evidence-tabs-header">
        <div className="evidence-tabs-component">{tabsComponent}</div>
        {showDateFilters && (
          <div className="evidence-tabs-filter">
            <AnalysisDateFilters
              activeTab={activeTab as 'OIL_ANALYSIS' | 'VIBRATION_ANALYSIS'}
              oilAnalysisData={oilAnalysisData ?? {}}
              vibrationAnalysisData={vibrationAnalysisData ?? {}}
              dateOptions={dateOptions}
            />
          </div>
        )}
      </div>
      <div className="evidence-tabs-content">{tabActive()}</div>
    </>
  );
};
